import React, { Component, Fragment } from 'react';
import { generateUUIDV4 } from '../../../../util/Generator';
import {
  ArchiosExpressionProfile,
  ExpressionBuilderView,
  ExpressionContextMenuComponent,
  ExpressionTokenComponent,
  StandardExpressionBuilderView,
  Syntax,
  TokenVariable,
  VariableToken,
} from '@trlogic/ui-pack';
import { VariableProperties } from '../../../../domain/datatype/VariablePropertiesDomains';
import VariableType from '../../../../constant/VariableType';
import _ from 'lodash';
import { createPortal } from 'react-dom';
import DataType from '../../../../constant/DataType';
import { MLOpsModel, MLOpsModelCustomFeature } from '../../../../domain/mlops/MLOpsDomains';
import { query, repository } from '@trlogic/client-js';
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import { EnrichmentBase, ListVariable, VelocityVariable } from '../../../../domain/enrichment/EnrichmentDomain';
import CollectionNames from '../../../../constant/CollectionNames';
import QueryableRepository = repository.QueryableRepository;
import QueryBuilder = query.QueryBuilder;
import or = query.or;
import and = query.and;
import eq = query.eq;
import { ExpressionUtil } from '../../../../util/ExpressionUtil';

class AiModelCustomFeatureExpressionComponent extends Component<Props, State> {
  public state: State = new State();

  private readonly id: string = `expression_builder_ai_${generateUUIDV4()}`;
  private readonly repository: QueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    const { expressionView } = this.state;
    if (expressionView == undefined) {
      return null;
    }
    return (
      <div id={this.id}>
        {expressionView && (
          <Fragment>
            {createPortal(<ExpressionContextMenuComponent context={expressionView.getContext()} close={this.onCloseContextMenu} />, document.body)}
            <ExpressionTokenComponent syntax={expressionView.getModel()} view={expressionView} />
          </Fragment>
        )}
      </div>
    );
  }

  public componentDidMount() {
    this.buildView();
  }

  private buildView = async () => {
    const { feature, returnType } = this.props;
    const expressionView: ExpressionBuilderView = new StandardExpressionBuilderView({
      el: `#${this.id}`,
      model: feature.expression,
      profile: new ArchiosExpressionProfile(),
      returnType: { dataType: DataType.UNKNOWN },
      onCtrlToken: ExpressionUtil.onNavigateToken,
    });
    const variables: any = await this.prepareVariables();
    expressionView.getContext().setVariables(variables);
    expressionView.onChange(this.onChangeSyntax as VoidFunction);
    this.setState({ expressionView }, () => {
      expressionView.initialize();
      if (this.props.disabled) {
        expressionView.pause();
      }
    });
  };

  public componentDidUpdate(prevProps: Readonly<Props>) {
    if (!_.isEqual(prevProps.aiModel.channel, this.props.aiModel.channel) || !_.isEqual(prevProps.update, this.props.update)) {
      this.updateVariables();
    }
    const { expressionView } = this.state;
    expressionView?.getContext().getEvaluateHelper()?.evaluateModel();
  }

  private onCloseContextMenu() {}

  private onChangeSyntax = (syntax: Syntax) => {
    const { feature, onChange } = this.props;
    feature.expression = syntax;
    onChange(feature);
  };

  private prepareVariables = async () => {
    const originalFeatures = this.props.aiModel.baseFeatures;
    const featureVariables = originalFeatures.map((v) => ({
      type: VariableType.AI_FEATURE_VARIABLE,
      variable: {
        type: 'Common Variable',
        variableType: VariableType.AI_FEATURE_VARIABLE,
        name: v.name,
        properties: v.properties.dataType == DataType.DATE ? { dataType: DataType.NUMBER } : v.properties,
      },
    }));
    const enrichments = await this.getEnrichments();
    const enrichmentVariables: Record<Exclude<VariableType, VariableType.AI_FEATURE_VARIABLE>, TokenVariable<VariableToken>[]> = _.groupBy(
      enrichments.map((e) => ({
        type: e.type,
        variable: {
          type: 'Common Variable',
          variableType: e.type,
          name: e.name,
          properties: e.properties,
          args: {},
        },
      })),
      (e: EnrichmentBase) => e.type,
    ) as Record<VariableType, any[]>;
    return {
      [VariableType.AI_FEATURE_VARIABLE]: featureVariables,
      ...enrichmentVariables,
    };
  };

  private getEnrichments = async (): Promise<EnrichmentBase[]> => {
    const { aiModel } = this.props;

    const baseFeatures = aiModel.baseFeatures.map((f) => f.name);
    if (baseFeatures.length == 0) return [];

    const enrichmentSubscriptionQuery = QueryBuilder.select()
      .from(CollectionNames.ENRICHMENTS)
      .where(
        or(and(eq('type', VariableType.LIST), eq('transaction', aiModel.channel)), and(eq('type', VariableType.VELOCITY), eq('transaction', aiModel.channel))),
      )
      .build();
    const enrichments = await this.repository.findMany<EnrichmentBase>(enrichmentSubscriptionQuery);

    const validEnrichments: EnrichmentBase[] = [];
    for (const enrichment of enrichments) {
      if (enrichment.type == VariableType.LIST) {
        const isValid = await this.checkSyntaxDoesNotContainNonBaseFeaturesOrOtherEnrichments((enrichment as ListVariable).key, baseFeatures);
        if (!isValid) continue;
        validEnrichments.push(enrichment);
      } else if (enrichment.type == VariableType.VELOCITY) {
        const isSelectValid = await this.checkSyntaxDoesNotContainNonBaseFeaturesOrOtherEnrichments((enrichment as VelocityVariable).select, baseFeatures);
        if (!isSelectValid) continue;
        const isGroupByValid = await this.checkSyntaxDoesNotContainNonBaseFeaturesOrOtherEnrichments((enrichment as VelocityVariable).groupBy, baseFeatures);
        if (!isGroupByValid) continue;
        const isWhereValid = await this.checkSyntaxDoesNotContainNonBaseFeaturesOrOtherEnrichments((enrichment as VelocityVariable).where, baseFeatures);
        if (!isWhereValid) continue;
        validEnrichments.push(enrichment);
      }
    }

    return validEnrichments;
  };

  private async checkSyntaxDoesNotContainNonBaseFeaturesOrOtherEnrichments(syntax: Syntax, baseFeatures: string[]): Promise<boolean> {
    if (syntax == undefined) return false;
    if (syntax.value.type == 'Common Variable') {
      const variableType = (syntax.value as VariableToken).variableType as string;
      if (variableType == VariableType.EVENT_VARIABLE) {
        return baseFeatures.includes((syntax.value as VariableToken).name);
      } else if (variableType == VariableType.LIST) {
        return this.checkListEnrichmentValid((syntax.value as VariableToken).name);
      } else {
        return variableType == VariableType.STREAM_ROW_VARIABLE;
      }
    }
    if (syntax.children) {
      for (const child of syntax.children) {
        if (!(await this.checkSyntaxDoesNotContainNonBaseFeaturesOrOtherEnrichments(child, baseFeatures))) {
          return false;
        }
      }
    }
    return true;
  }

  private checkListEnrichmentValid = async (name: string) => {
    const listEnrichmentQuery = QueryBuilder.select()
      .from(CollectionNames.ENRICHMENTS)
      .where(and(eq('name', name), eq('type', VariableType.LIST)))
      .build();
    const enrichment = await this.repository.find(listEnrichmentQuery);
    return this.checkSyntaxDoesNotContainNonBaseFeaturesOrOtherEnrichments(
      (enrichment as ListVariable).key,
      this.props.aiModel.baseFeatures.map((f) => f.name),
    );
  };

  private updateVariables = async () => {
    const { expressionView } = this.state;
    const variables = await this.prepareVariables();
    expressionView?.getContext().setVariables(variables as any);
  };
}

interface Props {
  aiModel: MLOpsModel;
  feature: MLOpsModelCustomFeature;
  onChange: (feature: MLOpsModelCustomFeature) => void;
  disabled?: boolean;
  returnType: VariableProperties;
  update: number;
}

class State {
  expressionView: ExpressionBuilderView | undefined;
  variables: Record<string, TokenVariable[]> = {};
}

export default AiModelCustomFeatureExpressionComponent;
